import { CdkDragDrop, copyArrayItem } from "@angular/cdk/drag-drop";
import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { SignalsService, TemplateDTO } from "src/app/signals/signals.service";
import { UiService } from "src/app/ui/ui.service";
import { Media } from "../../media.service";

@Component({
  selector: "app-template",
  templateUrl: "./template.component.html",
  styleUrls: ["./template.component.scss"],
})
export class TemplateComponent implements OnInit {
  templateTypeOptions = [
    {
      value: "mobilePush",
      name: "Mobile Push",
    },
  ];

  headerOpened: boolean = false;
  backgroundOpened: boolean = false;
  headerImages: Media[] | undefined;
  backgroundImages: Media[] | undefined;
  selectedHeadingImage: Media[] = [];
  selectedBgImage: Media[] = [];
  sampleText = false;
  redemptionCode = false;
  thumbs = false;
  thumbsColors = [
    "#46616e",
    "#9270A3",
    "#7DC4FD",
    "#3E627F",
    "#FF9226",
    "#FFC80F",
    "#A00000",
    "#767676",
    "#293A42",
    "#E6E6E6",
    "#779300",
  ];
  colorPickerColor!: string;
  selectedThumbColor: string = "#46616e";
  template: TemplateDTO | undefined;
  isView: boolean = false;
  isEdit: boolean = false;
  templateForm!: FormGroup;
  private onComponentDestroy = new Subject();

  constructor(
    private route: ActivatedRoute,
    private SessionService: SessionService,
    private SignalsService: SignalsService,
    private Router: Router,
    private NotificationService: NotificationService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.template = this.route.snapshot.data.template;

    if (this.template) {
      if (this.route.snapshot.data.isView) {
        this.isView = true;
      } else {
        this.isEdit = true;
      }
      this.selectedBgImage = [this.template.background];
      this.selectedHeadingImage = this.template.logo
        ? [this.template.logo]
        : [];
      this.thumbs = !!this.template.has_feedback;

      if (this.template.feedback_color) {
        if (!this.thumbsColors.includes(this.template.feedback_color)) {
          this.thumbsColors.push(this.template.feedback_color);
        }
        this.selectedThumbColor = this.template.feedback_color;
      }
    }

    this.templateForm = new FormGroup({
      templateType: new FormControl(this.isViewOrEdit ? "mobilePush" : "", [
        Validators.required,
      ]),
      templateName: new FormControl(this.template?.name, [Validators.required]),
    });

    if (this.isView) {
      this.templateForm.disable();
    }

    this.headerImages = this.route.snapshot.data.medias?.records.filter(
      (media: Media) => {
        return media.context === "signal_template_logo";
      },
    );

    this.backgroundImages = this.route.snapshot.data.medias?.records.filter(
      (media: Media) => {
        return media.context === "signal_template_background";
      },
    );
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get showThumbs() {
    return this.thumbs && this.selectedBgImage.length;
  }

  get showText() {
    return this.sampleText && this.selectedBgImage.length;
  }

  get saveDisabled() {
    return (
      this.templateForm.invalid ||
      !this.selectedHeadingImage.length ||
      !this.selectedBgImage.length
    );
  }

  get isMobilePush() {
    return this.templateForm.controls.templateType.value === "mobilePush";
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  get templateDTO(): TemplateDTO {
    return {
      account_app_id: this.SessionService.currentAppValue!.id,
      name: this.templateForm.controls.templateName.value,
      logo: this.selectedHeadingImage[0],
      background: this.selectedBgImage[0],
      has_feedback: this.thumbs,
      feedback_color: this.thumbs ? this.selectedThumbColor : undefined,
    };
  }

  get isViewOrEdit() {
    return this.isEdit || this.isView;
  }

  get isCreate() {
    return !this.isViewOrEdit;
  }

  toggleHeaderImages() {
    this.headerOpened = !this.headerOpened;
    this.backgroundOpened = false;
  }

  toggleBackgroundImages() {
    this.backgroundOpened = !this.backgroundOpened;
    this.headerOpened = false;
  }

  saveTemplate() {
    this.uiService
      .waitForEvent(
        this.SignalsService.createSignalTemplate(
          this.currentAccount.accountID,
          this.templateDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          this.NotificationService.openSuccessSnackBar("Template created");
          this.Router.navigate(["library"], { relativeTo: this.route.parent });
        },
        () => {
          this.NotificationService.openErrorSnackBar("Error creating Template");
        },
      );
  }

  updateTemplate() {
    this.uiService
      .waitForEvent(
        this.SignalsService.updateSignalTemplate(
          this.currentAccount.accountID,
          this.template!.id!,
          this.templateDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          this.NotificationService.openSuccessSnackBar("Template updated");
          this.Router.navigate(["library"], { relativeTo: this.route.parent });
        },
        () => {
          this.NotificationService.openErrorSnackBar("Error updating Template");
        },
      );
  }

  onHeaderDropped(event: CdkDragDrop<any, any>) {}

  onDrop(event: any) {
    if (event.previousContainer !== event.container) {
      //Empty out container data
      event.container.data.length = 0;
      copyArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  removeHeading() {
    this.selectedHeadingImage.length = 0;
  }

  removeBackground() {
    this.selectedBgImage.length = 0;
  }

  setThumbColor(color: string) {
    this.selectedThumbColor = color;
  }

  onColorPickerOK() {
    if (this.colorPickerColor) {
      if (!this.thumbsColors.includes(this.colorPickerColor)) {
        this.thumbsColors.push(this.colorPickerColor);
      }
      this.selectedThumbColor = this.colorPickerColor;
    }
  }
}
